/**
 * Line Item Builder — pure domain logic for constructing structured BOM
 * line items from receipt AI data, manual expense line items, or a fallback
 * single-line from the amount field.
 *
 * Extracted from ChildEntryForm to enable independent testing and reuse
 * across capture surfaces.
 */

import type { ReceiptAiPayload } from '@/lib/receiptAiAnalysis';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import type { MixedVatSplit } from './taxResolution';

export interface BomLineItem {
  item_name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  line_total: number;
  tax_rate: number;
  tax_amount: number;
  tax_inclusive: boolean;
  line_type: string;
  line_source: string;
  allocation_type?: string;
  linked_job_id?: string;
  linked_customer_id?: string;
  linked_customer_name?: string;
  expense_account_code?: string;
  is_billable?: boolean;
}

export interface LineItemBuilderInput {
  selectedEntryType: string | null;
  expenseLineItems: DocumentLineItem[];
  receiptResult: ReceiptAiPayload | null;
  amount?: number;
  notes?: string;
  entryLabel?: string;
  defaultTaxRate: number;
  /** When present, overrides per-line items with a Hubdoc-style two-line split */
  mixedVatSplit?: MixedVatSplit | null;
  /** Fuel-specific context for building a meaningful line item description
   *  when the OCR returns no line items (common for compact SA fuel slips). */
  fuelType?: string | null;
  litres?: number | string | null;
  station?: string | null;
  vehicleRegistration?: string | null;
}

/**
 * Build structured BOM line items from (in priority order):
 * 1. Manually entered/edited expense line items
 * 2. AI-extracted line items from receipt analysis
 * 3. Fallback: single line from amount
 */
export function buildBomLineItems(input: LineItemBuilderInput): BomLineItem[] {
  const { selectedEntryType, expenseLineItems, receiptResult, amount, notes, entryLabel, defaultTaxRate, mixedVatSplit, fuelType, litres, station, vehicleRegistration } = input;

  // ── Hubdoc-style mixed VAT split ──
  // When a receipt has mixed VAT treatment (some items vatable, some non-vatable),
  // instead of applying a single rate to all line items, create two summary lines:
  // one for the vatable portion at the actual VAT rate, one for the non-vatable at 0%.
  // This mirrors how Hubdoc handles mixed-rate receipts with its "Extracted Amount"
  // + Taxable/Untaxed portion approach.
  if (mixedVatSplit && receiptResult) {
    const { vatRate, taxableSubtotal, nonTaxableSubtotal, taxAmount, taxInclusive, itemCount } = mixedVatSplit;
    const items: BomLineItem[] = [];

    // Taxable portion
    if (taxableSubtotal > 0) {
      const taxableTaxAmount = taxInclusive
        ? Math.round((taxableSubtotal - (taxableSubtotal / (1 + vatRate / 100))) * 100) / 100
        : taxAmount;
      items.push({
        item_name: `Vatable items (${itemCount} items on receipt)`,
        description: `Vatable portion of ${receiptResult.vendor_name || 'receipt'} — ${itemCount} line items`,
        quantity: 1,
        unit: 'each',
        unit_cost: taxableSubtotal,
        line_total: taxableSubtotal,
        tax_rate: vatRate,
        tax_amount: taxableTaxAmount,
        tax_inclusive: taxInclusive,
        line_type: 'item',
        line_source: 'expense',
      });
    }

    // Non-taxable portion
    if (nonTaxableSubtotal > 0) {
      items.push({
        item_name: `Non-vatable items (${itemCount} items on receipt)`,
        description: `Non-vatable portion of ${receiptResult.vendor_name || 'receipt'} — ${itemCount} line items`,
        quantity: 1,
        unit: 'each',
        unit_cost: nonTaxableSubtotal,
        line_total: nonTaxableSubtotal,
        tax_rate: 0,
        tax_amount: 0,
        tax_inclusive: taxInclusive,
        line_type: 'item',
        line_source: 'expense',
      });
    }

    return items;
  }

  const isExpenseType = selectedEntryType != null;
  if (isExpenseType && expenseLineItems.length > 0) {
    return expenseLineItems.map((item, index) => {
      const qty = Number(item.quantity) || 1;
      const price = Number(item.unit_price) || 0;
      const lineTotal = item.line_total != null ? Number(item.line_total) : (qty * price);
      const itemTaxRate = item.tax_rate != null ? item.tax_rate : defaultTaxRate;
      return {
        item_name: String(item.description || `Line item ${index + 1}`).substring(0, 200),
        description: item.description || '',
        quantity: qty,
        unit: item.unit || 'each',
        unit_cost: price,
        line_total: lineTotal,
        tax_rate: itemTaxRate,
        tax_amount: item.tax_amount != null ? Number(item.tax_amount) : (lineTotal * (itemTaxRate / 100)),
        tax_inclusive: Boolean(item.tax_inclusive),
        line_type: 'item',
        line_source: 'expense',
        allocation_type: item.allocation_type,
        linked_job_id: item.linked_job_id,
        linked_customer_id: item.linked_customer_id,
        linked_customer_name: item.linked_customer_name,
        expense_account_code: item.expense_account_code,
        is_billable: item.is_billable,
      };
    });
  }

  if (receiptResult && Array.isArray(receiptResult.line_items) && receiptResult.line_items.length > 0) {
    return receiptResult.line_items.map((item, index) => {
      const qty = Number(item.quantity) || 1;
      const price = Number(item.unit_price) || 0;
      const lineTotal = Number(item.total) || (qty * price);
      // Respect per-line tax_rate from OCR if present, otherwise use default
      const itemTaxRate = item.tax_rate != null ? Number(item.tax_rate) : defaultTaxRate;
      // Capture-form rule: a scanned line item always displays the price
      // printed on the slip — never VAT-adjusted. Marking items inclusive
      // keeps line_total = printed total; DocumentModal's incl/excl
      // selector stays available for finance to flip the rare genuinely
      // exclusive document at review.
      const taxInclusive = (item as unknown as Record<string, unknown>).tax_inclusive != null
        ? Boolean((item as unknown as Record<string, unknown>).tax_inclusive)
        : true;
      // Calculate tax_amount based on tax_inclusive flag
      const taxAmount = taxInclusive
        ? Math.round((lineTotal - (lineTotal / (1 + itemTaxRate / 100))) * 100) / 100
        : Math.round((lineTotal * (itemTaxRate / 100)) * 100) / 100;
      return {
        item_name: String(item.description || `Line item ${index + 1}`).substring(0, 200),
        description: item.description || '',
        quantity: qty,
        unit: 'each',
        unit_cost: price,
        line_total: lineTotal,
        tax_rate: itemTaxRate,
        tax_amount: taxAmount,
        tax_inclusive: taxInclusive,
        line_type: 'item',
        line_source: 'expense',
      };
    });
  }

  if (amount && amount > 0) {
    // When the amount comes from a till slip total, it's typically VAT-inclusive.
    // Extract tax from the gross amount rather than adding tax on top.
    const taxAmount = Math.round((amount - (amount / (1 + defaultTaxRate / 100))) * 100) / 100;
    // Build a meaningful description for fuel slips when the OCR returned no
    // line items (common for compact SA fuel slips like "Diesel 50ppm P 8 15.41 R500.00").
    // Without this, the line item description is empty and shows as "line item 1".
    const fuelDescription = buildFuelLineDescription(fuelType, litres, station, vehicleRegistration);
    const fallbackDescription = notes || fuelDescription || '';
    const fallbackItemName = notes || fuelDescription || entryLabel || 'Expense';
    return [{
      item_name: String(fallbackItemName).substring(0, 200),
      description: fallbackDescription,
      quantity: 1,
      unit: 'each',
      unit_cost: amount,
      line_total: amount,
      tax_rate: defaultTaxRate,
      tax_amount: taxAmount,
      tax_inclusive: true,
      line_type: 'item',
      line_source: 'expense',
    }];
  }

  return [];
}

/**
 * Build a human-readable line item description for fuel slips from the
 * captured fuel context. Returns empty string when no fuel context is available.
 * Examples:
 *   "Diesel 50ppm — 15.41 L — Engen Doringkloof Mtr (HV10PVGP)"
 *   "Diesel — 15.41 L"
 *   "Fuel — Engen Doringkloof Mtr"
 */
function buildFuelLineDescription(
  fuelType?: string | null,
  litres?: number | string | null,
  station?: string | null,
  vehicleRegistration?: string | null,
): string {
  const parts: string[] = [];
  if (fuelType) parts.push(fuelType);
  if (litres != null && Number(litres) > 0) parts.push(`${litres} L`);
  if (station && vehicleRegistration) {
    parts.push(`${station} (${vehicleRegistration})`);
  } else if (station) {
    parts.push(station);
  } else if (vehicleRegistration) {
    parts.push(vehicleRegistration);
  }
  // Only return if we have at least fuel type or litres — otherwise it's not a fuel slip
  if (!fuelType && !litres) return '';
  return parts.join(' — ');
}

/**
 * Convert BOM line items to the intake API format.
 */
export function toIntakeLineItems(
  bomItems: BomLineItem[],
  defaultTaxRate: number,
): Array<{
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  tax_rate: number;
  tax_amount: number;
  tax_inclusive: boolean;
  allocation_type?: string;
  linked_job_id?: string;
  linked_customer_id?: string;
  linked_customer_name?: string;
  expense_account_code?: string;
  is_billable?: boolean;
}> {
  return bomItems.map((item) => ({
    description: item.description || '',
    quantity: item.quantity,
    unit_price: item.unit_cost,
    amount: item.line_total,
    tax_rate: item.tax_rate,
    tax_amount: item.tax_amount,
    tax_inclusive: item.tax_inclusive,
    allocation_type: item.allocation_type,
    linked_job_id: item.linked_job_id,
    linked_customer_id: item.linked_customer_id,
    linked_customer_name: item.linked_customer_name,
    expense_account_code: item.expense_account_code,
    is_billable: item.is_billable,
  }));
}
